/* eslint-disable no-fallthrough */
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
// import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import { isApiError, requestNapi, requestPuppy } from '@wix/da-http-client';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import ddt from '@wix/da-ddt';

import { Module } from '../../types/modules';
import {
  updateModule,
  fetchModuleEndpoint,
  fetchModuleEndpointSuccess,
  fetchModuleEndpointError,
  fetchModuleSuccess,
} from '../actions/modules';
import { getModuleVersion } from '../selectors/modules';
import { sampleModuleResponse } from '../sampleData';
import { isModuleResponse } from '../helpers';

// These are endpoints that write to draftJS they have to go via node
const POST_ENDPOINTS_THAT_NEED_NODE = [
  'about/save',
  'group_about/save',
  'papi_custombox/save',
  'donations/save',
  'papigroupabout/save',
  'megabox/save',
];

function* postModuleEndpoint(endpoint: string, params: any) {
  if (POST_ENDPOINTS_THAT_NEED_NODE.includes(endpoint)) {
    return yield call(requestNapi, {
      method: 'post',
      url: `/gruser/module/${endpoint}`,
      data: params,
    });
  }

  return yield call(
    requestPuppy,
    {
      method: 'post',
      url: `module/${endpoint}`,
      data: params,
    },
    () => sampleModuleResponse,
    'gruser'
  );
}

function* getModuleEndpoint(endpoint: string, params: any) {
  return yield call(
    requestPuppy,
    {
      method: 'get',
      url: `module/${endpoint}`,
      params,
    },
    undefined,
    'gruser'
  );
}

export function* runModuleInitActions(gruser, moduleResponse: Module) {
  // Duck typing
  if (!isModuleResponse(moduleResponse)) {
    return;
  }

  yield put(fetchModuleSuccess(gruser, moduleResponse));
}

export function* fetchModuleEndpointSaga(action) {
  const { gruser, ...payload } = action?.payload || {};
  const { id, path, method, options, ...data } = payload;

  const version = yield select(getModuleVersion, gruser, id);
  const { gruserId, gruserTypeid, gruserItemid } = gruser;
  const csrf = yield select(getConfigKey, 'csrfToken');

  const fetchEndpoint = method === 'get' ? getModuleEndpoint : postModuleEndpoint;

  const response = yield call(fetchEndpoint, path, {
    ...data,
    csrf_token: csrf,
    version,
    gruserid: gruserId,
    gruser_typeid: gruserTypeid,
    gruser_itemid: gruserItemid,
    moduleid: id,
  });

  if (!response || isApiError(response)) {
    const errorDescription =
      response?.errorDetails?.Form || response?.details || response?.errorDescription;

    ddt.log(`modules`, `fetch action error`, `${errorDescription}`);

    // yield putErrorNotification(errorDescription);
    yield put(
      fetchModuleEndpointError(gruser, {
        id,
        path,
        errorDescription,
      })
    );

    yield put(updateModule(gruser, { id, moduleMeta: { isFetching: false } }));

    return;
  }

  if (isModuleResponse(response)) {
    // update module data in the store
    yield runModuleInitActions(gruser, response);
  } else {
    // endpoint does not return a module response, hence, no module version gets updated
    // module version is used as memoization key for many hooks, so if no version
    ddt.log(`modules`, `save endpoint does not return a module response`, {
      id,
      path,
      response,
    });
  }
  yield put(fetchModuleEndpointSuccess(gruser, { id, response, path }));
}

export default function* rootModulesSaga() {
  yield all([takeEvery(fetchModuleEndpoint, fetchModuleEndpointSaga)]);
}
